import { EuiToolTip } from "@equipmentshare/ds2";
import { pipe } from "fp";
import React from "react";
import styled from "styled-components";

import * as Button from "~/Button";
import * as Data from "~/Data";
import * as Driver from "~/Driver";
import { Link as JobLink } from "~/Job";
import * as Layout from "~/Layout";
import * as Log from "~/Log";
import { NotApplicable } from "~/NotApplicable";
import * as Table from "~/Table";
import * as TimeTracking from "~/TimeTracking";
import { Link as WorkCodeLink } from "~/WorkCode";
import { WorkOrder as GenericWorkOrder } from "~/WorkOrder";

import { Time as DriverTime } from "~/Time";

import * as Interval from "./Interval";

export * as Interval from "./Interval";

export type Row = Data.Reference.Reference & Partial<Log.Event.Event>;
export type Column<Name extends keyof Row> = Pick<Row, Name>;

export const colSpan = (): number => Head.headers().length + 1;

export const Component: Component = ({ children }): React.ReactElement => (
  <Table.Component>
    <Head.Component />
    {children}
  </Table.Component>
);

export namespace Head {
  export const headers = () =>
    [
      "Status",
      "Time",
      "Location",
      "Source",
      "Work Order",
      "Work Codes",
      "Job",
      "Notes",
    ] as const;

  export const Component = () => (
    <Table.Head.Component>
      <Table.Row.Component>
        {[...headers(), ""].map((header) => (
          <Table.Header.Component key={header}>{header}</Table.Header.Component>
        ))}
      </Table.Row.Component>
    </Table.Head.Component>
  );
}

export namespace Row {
  export const Component: Component<Row> = (event) => (
    <>
      <Status.Component {...event} />
      <Time.Component {...event} />
      <Location.Component {...event} />
      <Source.Component {...event} />
      <WorkOrder.Component {...event} />
      <WorkCode.Component {...event} />
      <Job.Component {...event} />
      <Notes.Component {...event} />
    </>
  );
}

export namespace Status {
  export const Component: React.Fn<Column<"distance" | "status">> = ({
    distance,
    status,
  }) => (
    <Table.Column.Component>
      {status && <Driver.Status.Component status={status} />}
      {!!distance && (
        <Layout.Inline.Component left>
          {distance.toFixed(1)} mi
        </Layout.Inline.Component>
      )}
    </Table.Column.Component>
  );
}

export namespace Time {
  export const Component: React.Fn<Column<"interval">> = ({ interval }) =>
    !interval ? null : <Interval.Component interval={interval} />;
}

export namespace Location {
  export const Component: React.Fn<Column<"location">> = ({ location }) => (
    <Table.Column.Component>
      {location ?? <NotApplicable />}
    </Table.Column.Component>
  );
}

export namespace Source {
  export const Component: React.Fn<Column<"sources">> = ({ sources }) => (
    <Table.Column.Component>
      {sources && <Log.Event.Source.Component {...sources.current} />}
    </Table.Column.Component>
  );
}

/**
 * The time entry whose work belongs on this row, if any.
 *
 * `TimeTracking.Entry.Period` pairs the interval with the row's status, and
 * `useFromInterval` decides on both: overlap says which entry shares time with
 * the row, the status says whether the row could have produced work at all.
 *
 * A row with no status yields nothing — an unknown status is not a known
 * on-duty one, and this is payroll-adjacent data on a compliance-bearing page.
 */
const useWorkEntry = ({
  interval,
  status,
}: Column<"interval" | "status">): TimeTracking.Entry.Entry | undefined =>
  TimeTracking.Entry.useFromInterval(
    interval && status ? { interval, status } : undefined
  );

export namespace WorkOrder {
  export const Component: React.Fn<Column<"interval" | "status">> = (props) =>
    pipe(useWorkEntry(props), (entry) => (
        <Table.Column.Component>
          {entry?.workOrder && (
            <GenericWorkOrder.Link
              target="_blank"
              workOrder={entry.workOrder}
            />
          )}
        </Table.Column.Component>
      )
    );
}

export namespace WorkCode {
  export const Component: React.Fn<Column<"interval" | "status">> = (props) => {
    return pipe(useWorkEntry(props), (entry) => (
        <Table.Column.Component>
          {entry?.workCodes && entry?.workCodes.length > 0 && entry?.workCodes.map((workCode) => (
            <>
              <WorkCodeLink.Component
                target="_blank"
                workCode={workCode}
              />
              <br />
            </>
          ))}
        </Table.Column.Component>
      )
    );
  }
}

export namespace Job {
  export const Component: React.Fn<Column<"interval" | "status">> = ({
    interval,
    status,
    ...props
  }) =>
    pipe(useWorkEntry({ interval, status }), (entry) => (
        <Table.Column.Component>
          {entry?.job && (
            <JobLink.Component {...props} target="_blank" job={entry.job} />
          )}
        </Table.Column.Component>
      )
    );
}

export namespace Notes {
  export const Component: React.Fn<Column<"notes">> = ({ notes }) => (
    <Column.Component>{notes}</Column.Component>
  );

  namespace Column {
    export const Component = styled(Table.Column.Component)`
      width: 100%;
      white-space: normal !important;
      word-break: break-word;
      overflow-wrap: anywhere;
    `;
  }
}

export namespace Revise {
  export type Props = {
    readonly route: Log.Route;
    readonly event: Log.Event.Event;
    readonly onRevise: (
      revision: Log.Event.Event | Log.Suggestion.Suggestion,
      timezone?: DriverTime.Zone
    ) => void;
    readonly disabled?: boolean;
    readonly tooltip?: string;
  };
  export const Component: React.Fn<Props> = ({
    route,
    event,
    onRevise,
    disabled = false,
    tooltip,
  }) => {
    const driverZone = Driver.useFromReference({
      ...route.driver,
      missing: () => undefined,
      found: ({ time }) => time,
    });
    return (
      <Table.Column.Component>
        <EuiToolTip position="top" content={tooltip}>
          <span>
            <Button.Component
              action={!disabled}
              neutral={disabled}
              disabled={disabled}
              blank
              title="Revise"
              icon="$icon_pencil_fill"
              onClick={() => onRevise(event, driverZone)}
            />
          </span>
        </EuiToolTip>
      </Table.Column.Component>
    );
  };
}

export namespace Delete {
  export const Component = ({
    revision,
    onDelete,
  }: {
    readonly revision: Data.Reference.Reference;
    readonly onDelete: (suggestion: Data.Reference.Reference) => void;
  }) => (
    <Table.Column.Component>
      <Button.Component
        danger
        title="Delete"
        icon="$icon_delete_bin_fill"
        onClick={() => onDelete(revision)}
      />
    </Table.Column.Component>
  );
}
